/* Les natures de document, et ce qu'on accepte de recevoir.
 *
 * Pur, testé, sans réseau : le vocabulaire et les règles d'acceptation d'un
 * côté, le dépôt de l'autre.
 */

import type { NatureDocument } from "@/types/metier";

export const NATURES: { cle: NatureDocument; nom: string; aide: string }[] = [
  { cle: "presentation", nom: "Présentation",
    aide: "la plaquette qu'on envoie à un prospect" },
  { cle: "catalogue", nom: "Catalogue",
    aide: "la gamme complète, à faire parcourir" },
  { cle: "fiche_produit", nom: "Fiche produit",
    aide: "une référence précise, à rattacher au catalogue" },
  { cle: "fiche_technique", nom: "Fiche technique",
    aide: "ce que la qualité et la douane réclament" },
  { cle: "autre", nom: "Autre", aide: "certificat, contrat, correspondance" },
];

const NOMS: Record<NatureDocument, string> = NATURES.reduce(
  (m, n) => ({ ...m, [n.cle]: n.nom }),
  {} as Record<NatureDocument, string>,
);

export function nomNature(n: NatureDocument): string {
  return NOMS[n] ?? "Autre";
}

/* Dans l'ordre, du plus précis au plus large : « fiche technique produit » est
   d'abord une fiche technique, et c'est la première règle qui gagne. */
const INDICES: { nature: NatureDocument; signe: RegExp }[] = [
  { nature: "fiche_technique",
    signe: /\btds\b|technical|technique|\bspec|analyse|certificat d|coa\b/ },
  { nature: "fiche_produit",
    signe: /fiche[\s_-]*produit|product[\s_-]*(sheet|data|info)|\bfp\b/ },
  { nature: "catalogue",
    signe: /catalog|gamme|tarif|price[\s_-]*list|pricelist|assortiment/ },
  { nature: "presentation",
    signe: /present|plaquette|brochure|company[\s_-]*profile|profil/ },
];

/** Ce qu'on devine de la nature d'un fichier à son nom, pour ne pas la demander. */
export function natureProbable(nom: string): NatureDocument {
  const n = nom.toLowerCase();
  for (const i of INDICES) if (i.signe.test(n)) return i.nature;
  return "autre";
}

export const POIDS_MAX = 25 * 1024 * 1024;

const EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".png", ".jpg", ".jpeg", ".webp", ".zip",
];

/** Pourquoi ce fichier ne peut pas être déposé, ou rien s'il le peut. */
export function refusDe(fichier: { name: string; size: number }): string | null {
  const nom = fichier.name.toLowerCase();
  if (!EXTENSIONS.some((e) => nom.endsWith(e))) {
    return "Format non accepté. PDF, Word, Excel, PowerPoint, image ou zip.";
  }
  if (fichier.size > POIDS_MAX) {
    return `Fichier trop lourd (${poidsCourt(fichier.size)}). Le maximum est ${poidsCourt(POIDS_MAX)}.`;
  }
  if (fichier.size === 0) return "Fichier vide.";
  return null;
}

/** Un poids lisible : on ne montre pas 1 428 571 octets à quelqu'un. */
export function poidsCourt(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / 1024 / 1024).toFixed(octets < 10 * 1024 * 1024 ? 1 : 0)} Mo`;
}

/** Le titre proposé : le nom du fichier sans son extension ni ses tirets. */
export function titreProbable(nom: string): string {
  const sansExt = nom.replace(/\.[^.]+$/, "");
  const propre = sansExt.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return propre ? propre.charAt(0).toUpperCase() + propre.slice(1) : nom;
}
